import { compact, isUndefined } from 'lodash';
import path from 'path-browserify';

import { SchoolCardFragment, SchoolInfoBoxFragment, Strapi_Enum_School_Status } from '../../graphql.schema';

export const flatSpread = (item: any, prefix = '') =>
  Array.from([item])
    .flat()
    .filter((i) => !!i)
    .map((e) => `${prefix}${e}`)
    .join(' ')
    .trim();

export interface Modifiers {
  has?: string[] | string;
  is?: string[] | string;
  reversed?: boolean;
}

export const modifierClasses = (modifiers: Modifiers = {}) =>
  [flatSpread(modifiers.has, 'has-'), flatSpread(modifiers.is, 'is-')].join(' ').trim();

export const getS3DefaultImageUrl = (filename: string) => {
  const parts = filename.split('/');
  const basename = parts[parts.length - 1];
  return `https://wse-default-images-wse-dev.s3-eu-west-1.amazonaws.com/${basename}`;
};

export const fullImageUrl = (url?: string) => {
  if (!url) return;
  if (url.match(/^https?:\/\/.+/)) return url;
  if (process.env.GATSBY_STRAPI_ENDPOINT) {
    const origin = new URL(process.env.GATSBY_STRAPI_ENDPOINT).origin;
    return `${origin}${url}`;
  }
  return `http://localhost:1337${url}`;
};

export type GeoPoint = {
  lat: number;
  lng: number;
};

export const getMapMarkersFromSchools = (schools: SchoolCardFragment[]) =>
  compact(
    schools.map((school) => {
      const { slug, name, documentId, address, status, mapMarker } = school;
      const lat = mapMarker?.mapLocation?.latitude || 0;
      const lng = mapMarker?.mapLocation?.longitude || 0;
      const popUpHtml = mapMarker?.mapLocation?.popUpHtml ?? '';
      if (isUndefined(documentId) || isUndefined(lat) || isUndefined(lng)) return undefined;
      return {
        documentId,
        lat,
        lng,
        slug,
        name,
        address,
        popUpHtml,
        mapMarker,
        isVirtualSchool: status === Strapi_Enum_School_Status.open_virtually,
      };
    }),
  );

type SchemaData = {
  [key: string]: any;
};

export const hasNonNull = (obj?: { [key:string]: any }) => {
  if (!obj) return false

  return Object.entries(obj)
  .some(([key, value]) => {
    if (key === "id") return false;
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== "";
  });
}

export const generateSchemaForHomepage = (seoData: SchemaData) => {
  const schemaTypes = {
    "@context": "https://schema.org",
    ...(seoData?.homepage?.type && { "@type": seoData.homepage.type }),
    ...(seoData?.homepage?.name && { "name": seoData.homepage.name }),
    ...(seoData?.homepage?.url && { "url": seoData.homepage.url }),
    ...(seoData?.homepage?.email && { "email": seoData.homepage.email }),
    ...(seoData?.homepage?.address && { "address": seoData.homepage.address }),
    ...(seoData?.homepage?.telephone && { "telephone": seoData.homepage.telephone }),
  };

  return JSON.stringify(schemaTypes);
}

export const generateSchemaForCouresesIndex = (seoData: SchemaData, courses: any[]) => {
  const schemaTypes = {
    "@context": "https://schema.org",
    ...(seoData?.courses_index?.type && { "@type": seoData.courses_index.type }),
    ...(seoData?.courses_index?.name && { "name": seoData.courses_index.name }),
  };


  if (seoData?.courses_index?.pull_courses_list) {
    schemaTypes["itemListElement"] = [ ...courses.map((course: any) => course.name) ];
  } else if (seoData?.courses_index?.courses?.length > 0) {
    schemaTypes["itemListElement"] = [ ...seoData?.courses_index.courses.map((course: any) => course.name) ];
  }

  return JSON.stringify(schemaTypes);
}

export const generateSchemaForCourse = (course: any, pageContext: any, seoData: SchemaData) => {
  const atType: string[] = [];

  let courseSchema = undefined;
  if (seoData?.course?.active) {
    seoData?.course?.type && atType.push(seoData?.course.type);
    
    courseSchema = {
      "url": `${process.env.GATSBY_SITE_URL}/${pageContext?.breadcrumb?.location || ''}`,
      ...(course.name && { "name": course.name }),
      ...(course.short && { "description": course.short }),
      ...(course.image?.url && { "image": course.image?.url }),
      ...((seoData?.homepage?.type && seoData?.homepage?.name) && {
          "provider": {
            "@type": seoData?.homepage.type,
            "name": seoData?.homepage.name,
          }
      })
    };
  }

  let breadcrumbSchema = undefined;
  if(seoData?.course?.breadcrumb_active) {
    atType.push('BreadcrumbList')
    breadcrumbSchema = {
      itemListElement: [
        ...(pageContext?.breadcrumb?.crumbs
          ?.filter((crumb: any) => crumb.crumbLabel && crumb.pathname !== '/')
          ?.map((crumb: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.crumbLabel.replace(/-/g, ' '),
            "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
          })) ?? [])
      ]
    }
  }

  const schema: any = {
    "@context": "https://schema.org",
    "@type": atType,
    ...(breadcrumbSchema && breadcrumbSchema),
    ...(courseSchema && courseSchema),
  };
  
  return JSON.stringify(schema);
};

export const generateSchemaForSchoolIndex = (schools: any[], seoData: SchemaData) => {
  const schemaTypes = {
    "@context": "https://schema.org",
    ...(seoData?.school_index?.type && { "@type": seoData?.school_index.type }),
    ...(seoData?.school_index?.name && { "name": seoData?.school_index.name }),
    ...(seoData?.school_index?.pull_school_list && { "itemListElement": [ ...schools.map((school: any) => school.name) ] }),
    ...(!seoData?.school_index?.pull_school_list && seoData?.school_index?.school_list?.length > 0 && { "itemListElement": [ ...seoData?.school_index?.school_list.map((school: any) => school.name) ] }),
  };

  return JSON.stringify(schemaTypes);
}

export const generateSchemaForSchool = (school: SchoolCardFragment, pageContext: any, seoData: SchemaData) => {
  const atGraph = [];

  if (seoData?.school?.type && school.school_status === 'open_physically') {
    const schoolSchema = {
      "@type": seoData?.school?.type,
      "name": school.name,
      "telephone": school.phoneNumber,
      "email": school.emailAddress,
      "address": {
        "@type": "PostalAddress",
        ...(school?.address?.locality && { "addressLocality": school.address.locality }),
        ...(school?.address?.region && { "addressRegion": school.address.region }),
        ...(school?.address?.postalCode && { "postalCode": school.address.postalCode }),
        ...(school?.address?.streetAddress && { "streetAddress": school.address.streetAddress }),
        ...(school?.address?.country && { "addressCountry": school.address.country })
      },
      "geo": school.mapMarker?.mapLocation
      ? {
        "@type": "GeoCoordinates",
        "latitude": school.mapMarker.mapLocation.latitude,
        "longitude": school.mapMarker.mapLocation.longitude
      }
      : undefined,
    };

    atGraph.push(schoolSchema)
  }

  const breadcrumbListSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      ...(pageContext?.breadcrumb?.crumbs
        ?.filter((crumb: any) => crumb.crumbLabel && crumb.pathname !== '/')
        ?.map((crumb: any, index: number) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.crumbLabel.replace(/-/g, ' '),
          "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
        })) ?? []),

    ]
  };
  atGraph.push(breadcrumbListSchema)

  return JSON.stringify({
    "@context": "https://schema.org/",
    "@graph": atGraph
  });
}

export const generateSchemaForBreadcrumb = (entry:any,pageContext: any) => {
  const schema: any = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "headline": entry.title,
          "image": [entry.image?.url],
          "datePublished": entry.publishedAt,
          "dateModified": entry.updatedAt
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            ...(pageContext?.breadcrumb?.crumbs
              ?.filter((crumb: any) => crumb.crumbLabel && crumb.pathname !== '/')
              ?.map((crumb: any, index: number) => ({
                "@type": "ListItem",
                "position": index + 1,
                "name": crumb.crumbLabel.replace(/-/g, ' '),
                "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
              })) ?? [])
          ]
        }
      ]
  };
  return JSON.stringify(schema);
}
 
export const generateSchemaForEvent = (event: any, pageContext:any) => {
  const Schema :any = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": pageContext?.breadcrumb?.crumbs?.map((crumb: any, index: number) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.crumbLabel.replace(/-/g, ' '),
      "item": `${process.env.GATSBY_SITE_URL}${crumb.pathname}`
    }))
  };
  return Schema;
}

export const addTrailingSlash = (url: string): string => {
    // Skip if URL already ends with slash
  if (url.endsWith('/')) {
    return url;
  }

  // Skip if URL looks like a file (has .ext at the end)
  const lastSegment = url.split('/').pop() || '';
  if (lastSegment.includes('.')) {
    return url;
  }

  // Otherwise, add trailing slash
  return `${url}/`;
};